// Central allowlist of hosts we trust for cross-page/cross-subdomain
// redirects (login handoff, API-driven redirects, etc).
//
// Derived automatically from VITE_APP_API_BASE_URL — each environment
// trusts its own domain with zero extra config needed.

const getApiHostSuffix = (): string | null => {
  const apiBaseUrl = import.meta.env.VITE_APP_API_BASE_URL;
  if (!apiBaseUrl) return null;
  try {
    return `.${new URL(apiBaseUrl).hostname}`;
  } catch {
    return null;
  }
};

const apiHostSuffix = getApiHostSuffix();

if (!apiHostSuffix && import.meta.env.DEV) {
  console.warn(
    "[trustedOrigins] VITE_APP_API_BASE_URL is missing or invalid. " +
      "No cross-domain redirects will be trusted (besides localhost in dev).",
  );
}

export function isTrustedRedirectHost(hostname: string): boolean {
  // Allow any *.localhost subdomain, but ONLY in dev builds — this
  // check is compiled away entirely in production bundles.
  if (import.meta.env.DEV && hostname.endsWith(".localhost")) {
    return true;
  }

  return apiHostSuffix !== null && hostname.endsWith(apiHostSuffix);
}

/**
 * Validates a redirect target before it's ever passed to
 * window.location.replace/href. Accepts same-origin relative paths
 * (e.g. "/dashboard") and absolute URLs whose host matches our own API
 * domain. Rejects everything else (protocol-relative URLs, unknown
 * hosts, javascript: URIs, malformed input).
 */
export function resolveTrustedRedirectUrl(rawLocation: string): string | null {
  if (!rawLocation) return null;
  try {
    const resolved = new URL(rawLocation, window.location.origin);
    if (resolved.protocol !== "http:" && resolved.protocol !== "https:")
      return null;
    if (resolved.origin === window.location.origin) return resolved.toString();
    if (isTrustedRedirectHost(resolved.hostname)) return resolved.toString();
    return null;
  } catch {
    return null;
  }
}
